import { useState, type Dispatch, type SetStateAction } from 'react';
import { useRememberedState } from '../../hooks/useRemembered';
import type { DocumentType } from '../../types';
import type { Filter } from './invoicesViewShared';
import { invoiceListFiltersActive, type InvoiceListSort } from './invoiceListLogic';

/** Filter, Suche, Sortierung und Seite der Belegliste samt Settern. */
export interface InvoiceListFilterState {
    filter: Filter;
    setFilter: Dispatch<SetStateAction<Filter>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    customerFilter: string;
    setCustomerFilter: Dispatch<SetStateAction<string>>;
    typeFilter: DocumentType | 'all';
    setTypeFilter: Dispatch<SetStateAction<DocumentType | 'all'>>;
    dateFrom: string;
    setDateFrom: Dispatch<SetStateAction<string>>;
    dateTo: string;
    setDateTo: Dispatch<SetStateAction<string>>;
    sort: InvoiceListSort;
    setSort: Dispatch<SetStateAction<InvoiceListSort>>;
    pageSize: number;
    setPageSize: Dispatch<SetStateAction<number>>;
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    filtersActive: boolean;
    resetFilters: () => void;
}

/**
 * Der gemerkte Zustand der Belegliste.
 *
 * Eigener Hook, damit die Ansicht nur noch liest: die Schluessel
 * (`invoices.filter` usw.) und das Zuruecksetzen der Seite haengen zusammen
 * und gehoeren an eine Stelle. Die Seite selbst wird bewusst nicht gemerkt.
 */
export const useInvoiceListFilters = (): InvoiceListFilterState => {
    const [filter, setFilter] = useRememberedState<Filter>('invoices.filter', 'all');
    const [search, setSearch] = useRememberedState('invoices.search', '');
    const [customerFilter, setCustomerFilter] = useRememberedState<string>('invoices.customer', 'all');
    const [typeFilter, setTypeFilter] = useRememberedState<DocumentType | 'all'>('invoices.type', 'all');
    const [dateFrom, setDateFrom] = useRememberedState('invoices.dateFrom', '');
    const [dateTo, setDateTo] = useRememberedState('invoices.dateTo', '');
    // Absteigend nach Nummer: der neueste Beleg steht oben, und Entwuerfe —
    // die noch keine Nummer haben — davor. Das ist die Reihenfolge, in der man
    // die Liste liest.
    const [sort, setSort] = useRememberedState<InvoiceListSort>('invoices.sort', {
        key: 'number',
        dir: 'desc',
    });
    // Seitenweise Anzeige. Alle Belege sind geladen und gefiltert — begrenzt
    // wird, was gleichzeitig in der Tabelle steht: bei mehreren hundert Zeilen
    // kostet das Rendern spuerbar Zeit, und gelesen wird ohnehin nur der Anfang.
    // Summen und Zaehler bleiben auf der VOLLEN Trefferliste, sonst beantwortete
    // die Fusszeile eine andere Frage als der Filter stellt.
    const [pageSize, setPageSize] = useRememberedState('invoices.pageSize', 25);
    const [page, setPage] = useState(1);

    // Nach jeder Aenderung an Filter, Suche oder Seitengroesse wieder vorn
    // anfangen — Seite 7 einer neuen Trefferliste ist eine willkuerliche Stelle.
    // Sortierung bewusst NICHT dabei: dort will man dieselbe Stelle in anderer
    // Ordnung sehen.
    //
    // Waehrend des Renderns statt im Effekt (Muster "Zustand aus vorherigem
    // Render", wie in CompanyMergeAssistant): ein Effekt setzte die Seite erst
    // nach dem Zeichnen zurueck und zeichnete dann ein zweites Mal. Verglichen
    // wird Feld fuer Feld wie in einem Dep-Array.
    const pageResetDeps = [filter, search, customerFilter, typeFilter, dateFrom, dateTo, pageSize];
    const [knownPageResetDeps, setKnownPageResetDeps] = useState(pageResetDeps);
    if (pageResetDeps.some((value, i) => !Object.is(value, knownPageResetDeps[i]))) {
        setKnownPageResetDeps(pageResetDeps);
        setPage(1);
    }

    const filtersActive = invoiceListFiltersActive({
        filter,
        search,
        customerFilter,
        typeFilter,
        dateFrom,
        dateTo,
    });

    const resetFilters = () => {
        setFilter('all');
        setCustomerFilter('all');
        setTypeFilter('all');
        setDateFrom('');
        setDateTo('');
        setSearch('');
    };

    return {
        filter,
        setFilter,
        search,
        setSearch,
        customerFilter,
        setCustomerFilter,
        typeFilter,
        setTypeFilter,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        sort,
        setSort,
        pageSize,
        setPageSize,
        page,
        setPage,
        filtersActive,
        resetFilters,
    };
};
